import { Bot, User } from "lucide-react";
import type * as React from "react";
import { createContext, use, useCallback, useState } from "react";

import { AgentHandoffDialog } from "@/components/AgentHandoffPanel";
import { useListAgentDialog } from "@/components/ListAgentDialog";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";

interface AddAgentDialogContextValue {
	openAddAgentDialog: () => void;
	closeAddAgentDialog: () => void;
	isOpen: boolean;
}

const AddAgentDialogContext = createContext<AddAgentDialogContextValue | null>(
	null,
);

export function useAddAgentDialog() {
	const context = use(AddAgentDialogContext);
	if (!context) {
		throw new Error(
			"useAddAgentDialog must be used within an AddAgentDialogProvider",
		);
	}
	return context;
}

export function AddAgentDialogProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [isOpen, setIsOpen] = useState(false);
	const [isAgentHandoffDialogOpen, setIsAgentHandoffDialogOpen] =
		useState(false);
	const { openListAgentDialog } = useListAgentDialog();

	const openAddAgentDialog = useCallback(() => setIsOpen(true), []);
	const closeAddAgentDialog = useCallback(() => setIsOpen(false), []);

	const handleAddAsUser = () => {
		setIsOpen(false);
		openListAgentDialog("user", { onBack: () => setIsOpen(true) });
	};

	const handleAddAsAgent = () => {
		setIsOpen(false);
		setIsAgentHandoffDialogOpen(true);
	};

	return (
		<AddAgentDialogContext.Provider
			value={{ openAddAgentDialog, closeAddAgentDialog, isOpen }}
		>
			{children}
			<Dialog open={isOpen} onOpenChange={setIsOpen}>
				<DialogContent className="max-w-md gap-0 rounded-none border-border p-0">
					<div className="flex items-center justify-between border-b border-border px-5 py-3">
						<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							&gt; Add agent
						</span>
					</div>
					<DialogHeader className="space-y-2 p-5 text-left">
						<DialogTitle className="sr-only">Add agent</DialogTitle>
						<DialogDescription className="text-base leading-[1.55]">
							Verify your MRR, earn a dofollow backlink, and get discovered by
							users and AI agents.
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-px border-y border-border bg-border">
						<button
							type="button"
							onClick={handleAddAsUser}
							className="group flex items-center gap-4 bg-card px-5 py-4 text-left transition-[background-color,transform] hover:bg-brand-soft active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
						>
							<span className="flex size-9 shrink-0 items-center justify-center border border-border bg-background font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors group-hover:border-brand/40 group-hover:text-brand">
								01
							</span>
							<span className="min-w-0 flex-1 space-y-0.5">
								<span className="flex items-center gap-2 font-mono text-base uppercase tracking-[0.08em] text-foreground">
									<User className="size-3.5 shrink-0" />
									I'm a user
								</span>
								<span className="block text-sm text-muted-foreground">
									Add a listing. Connect revenue verification now or later.
								</span>
							</span>
							<span className="font-mono text-sm text-muted-foreground transition-[color,transform] group-hover:translate-x-0.5 group-hover:text-brand">
								→
							</span>
						</button>
						<button
							type="button"
							onClick={handleAddAsAgent}
							className="group flex items-center gap-4 bg-card px-5 py-4 text-left transition-[background-color,transform] hover:bg-brand-soft active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
						>
							<span className="flex size-9 shrink-0 items-center justify-center border border-border bg-background font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors group-hover:border-brand/40 group-hover:text-brand">
								02
							</span>
							<span className="min-w-0 flex-1 space-y-0.5">
								<span className="flex items-center gap-2 font-mono text-base uppercase tracking-[0.08em] text-foreground">
									<Bot className="size-3.5 shrink-0" />
									I'm an agent
								</span>
								<span className="block text-sm text-muted-foreground">
									Get a prompt you can give to your agent.
								</span>
							</span>
							<span className="font-mono text-sm text-muted-foreground transition-[color,transform] group-hover:translate-x-0.5 group-hover:text-brand">
								→
							</span>
						</button>
					</div>
				</DialogContent>
			</Dialog>
			<AgentHandoffDialog
				open={isAgentHandoffDialogOpen}
				onOpenChange={setIsAgentHandoffDialogOpen}
			/>
		</AddAgentDialogContext.Provider>
	);
}
